import {
  ClockIcon,
  ExclamationTriangleIcon,
  PuzzlePieceIcon
} from '@heroicons/react/24/outline'
import PropTypes from 'prop-types'
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getAppSettings } from '../../../constants/app-settings'
import trackEvents from '../../../constants/track-events'
import {
  useAivyContext,
  useLanguageContext,
  useNotificationContext
} from '../../../context'
import { mutation, query } from '../../../graphql'
import { convertNumberToTime, notifyBugsnag } from '../../../helpers'
import { trackEvent } from '../../../helpers/analytics'
import {
  buildChallengesForAppSettings,
  isDraftEqual,
  parseJsonObject,
  readAdjustmentBaseline,
  withOverrideDefaults,
  withoutOverrideDefaults
} from '../../../helpers/career-adjustment'
import {
  getCulturalFitMode,
  testBatteryConfig
} from '../../../helpers/diagnostic'
import { useActiveSection } from '../../../hooks/use-active-section'
import { useCareerAdjustment } from '../../../hooks/use-career-adjustment'
import { useCreateCareerNavigation } from '../../../hooks/use-create-career-navigation'
import { useMount } from '../../../hooks/use-mount'
import Loader from '../../activity-indicator'
import Button from '../../tailwind/Button'
import CreateCareerFooter from '../CreateCareerFooter'
import CompactStats from '../pieces/compact-stats'
import CustomizationTestBattery from './CustomizationTestBattery'
import HardnessOfSelection from './HardnessOfSelection'
import MustCriteriaSelection from './MustCriteriaSelection'
import SaveStateIndicator from './SaveStateIndicator'
import SectionNav from './SectionNav'

const I18N_PREFIX = 'create_career.adjustment.'

// Die Abschnitts-Ids sind zugleich die DOM-Ids fuer die Sprungnavigation.
const SECTIONS = [
  {
    id: 'customize-test-battery',
    trackingId: 'CUSTOMIZE_TEST_BATTERY',
    label: 'tab_title_customize_testbattery'
  },
  {
    id: 'selection-hardness',
    trackingId: 'SELECTION_HARDNESS',
    label: 'tab_title_selection_hardness'
  },
  {
    id: 'selection-must-criteria',
    trackingId: 'SELECTION_MUST_CRITERIA',
    label: 'tab_title_selection_must_criteria'
  }
]

const SECTION_IDS = SECTIONS.map(({ id }) => id)

/**
 * Expertenmodus (career.status === 'WAIT_CONFIG').
 *
 * Diese Komponente ist der einzige Owner des Drafts. Vorher hielt jeder der
 * drei Tabs eine eigene Formik-Instanz mit eigenem Speichern-Button und
 * schrieb den kompletten career_analyse-Blob, waehrend der Footer-Button
 * override_defaults vom Server-Objekt las. Wer nur den Footer klickte, verlor
 * Haerte und Muss-Kriterien ersatzlos, und ein Tabwechsel verwarf alles
 * Ungespeicherte.
 *
 * Jetzt: eine Quelle, ein Writer, kein Speichern-Button. Geschrieben wird
 * debounced nach override_defaults, der Draft-Ebene der Stelle, die erst
 * durch synthesizeAnalyze wirksam wird.
 *
 * Die drei Bereiche stehen untereinander statt in Tabs. Ein Tab verbirgt,
 * dass es die anderen beiden ueberhaupt gibt, und die Konfiguration ist eine
 * Entscheidung, keine drei. Weil das Challenge-Grid den Grossteil der Hoehe
 * ausmacht, haelt eine sticky Navigation die kurzen Bereiche erreichbar.
 */
const Adjustment = ({ career, careerMutation }) => {
  const { t } = useTranslation()
  const { alert, error } = useNotificationContext()
  const { system } = useAivyContext()
  const { language } = useLanguageContext()

  const sections = SECTIONS.map(({ id, label }) => ({
    id,
    name: t(I18N_PREFIX + label)
  }))

  const { activeId, scrollToSection } = useActiveSection(SECTION_IDS)
  const [isLoadingComplete, setIsLoadingComplete] = useState(false)
  const submittingRef = useRef(false)
  const synthesizedRef = useRef(false)

  useMount(() => {
    trackEvent(trackEvents.CREATE_CAREER_STEP_4_VIEWED, { mode: 'expert' })
  })

  const { isLoadingBack, handleBackNavigation, handleNextNavigation } =
    useCreateCareerNavigation({ career, careerMutation })

  const careerAnalyse = useMemo(
    () => parseJsonObject(career.career_analyse),
    [career.career_analyse]
  )

  const systemDimensionsData = useMemo(
    () => JSON.parse(system.dimensions_pdf_reports.data),
    [system.dimensions_pdf_reports.data]
  )

  // Der Katalog aus dem Backend-System-Item CHALLENGES. Einmal hier
  // abgeleitet, damit Anzeige und gespeicherte Konfiguration dieselbe Menge
  // sehen. Fehlt dort ein Eintrag, den testBatteryConfig als standard fuehrt
  // (z.B. CULTURE in einer nicht geseedeten Stage), zeigt der Schritt dafuer
  // keine Karte und die Kandidaten-App darf ihn dann auch nicht bekommen.
  const availableExamIds = useMemo(
    () =>
      Object.values(parseJsonObject(system.challenges.data))
        .map(({ exam_id }) => exam_id)
        .filter(Boolean),
    [system.challenges.data]
  )

  const configuration = useMemo(
    () =>
      testBatteryConfig(
        careerAnalyse.scores || {},
        getCulturalFitMode(career.app_settings)
      ),
    [careerAnalyse.scores, career.app_settings]
  )

  const adjustment = useCareerAdjustment({
    career,
    configuration,
    systemDimensionsData,
    availableExamIds
  })

  const baseline = useMemo(
    () =>
      readAdjustmentBaseline(career, {
        configuration,
        systemDimensionsData,
        availableExamIds
      }),
    // Nur beim ersten Lesen relevant, der Snapshot aendert sich im Schritt nicht.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const trackLeave = (event) => {
    const draft = adjustment.draftRef.current

    trackEvent(event, {
      challenges: draft.selected_challenges.length,
      challenges_before: baseline.selected_challenges.length,
      must_criteria: draft.mandatory_criteria.length,
      must_criteria_before: baseline.mandatory_criteria.length,
      difficulty: draft.selection_difficulty,
      difficulty_before: baseline.selection_difficulty,
      changed: !isDraftEqual(draft, baseline)
    })
  }

  const handleSectionSelect = (id) => {
    scrollToSection(id)
    trackEvent(trackEvents.CREATE_CAREER_ADJUSTMENT_TAB_CHANGED, {
      tab: SECTIONS.find((section) => section.id === id)?.trackingId
    })
  }

  const toggleChallenge = (exam_id) => {
    adjustment.toggleChallenge(exam_id)
    trackEvent(trackEvents.CREATE_CAREER_ADJUSTMENT_CHALLENGE_TOGGLED, {
      exam_id,
      selected: !adjustment.selectedExamIds.includes(exam_id),
      count: adjustment.draftRef.current.selected_challenges.length
    })
  }

  const setSelectionDifficulty = (difficulty) => {
    trackEvent(trackEvents.CREATE_CAREER_ADJUSTMENT_DIFFICULTY_CHANGED, {
      from: adjustment.selectionDifficulty,
      to: difficulty
    })
    adjustment.setSelectionDifficulty(difficulty)
  }

  const toggleCriterion = (dimension) => {
    adjustment.toggleCriterion(dimension)
    trackEvent(trackEvents.CREATE_CAREER_ADJUSTMENT_MUST_CRITERION_TOGGLED, {
      dimension,
      enabled: !adjustment.mandatoryCriteria.includes(dimension),
      count: adjustment.draftRef.current.mandatory_criteria.length
    })
  }

  const abort = () => {
    alert(
      t(I18N_PREFIX + 'modal_cancel_title'),
      [
        t(I18N_PREFIX + 'modal_cancel_text_0'),
        t(I18N_PREFIX + 'modal_cancel_text_1')
      ],
      [
        {
          onPress: async () => {
            // Vor dem Null-Write: ein nachlaufender Debounce wuerde
            // override_defaults sonst direkt wieder herstellen.
            await adjustment.freeze()

            // expert_mode_baseline faellt mit dem Verwerfen weg, beim
            // naechsten Eintritt schreibt confirm-testbattery ihn neu.
            const { expert_mode_baseline, ...meta } = parseJsonObject(
              career.customer_dashboard_meta
            )

            trackLeave(trackEvents.CREATE_CAREER_ADJUSTMENT_ABORTED)

            careerMutation.mutate(
              {
                input: {
                  id: career.id,
                  career_analyse: withoutOverrideDefaults(
                    career.career_analyse
                  ),
                  customer_dashboard_meta: JSON.stringify({
                    ...meta,
                    expert_mode_used: false
                  })
                }
              },
              {
                onSuccess: () =>
                  handleBackNavigation({
                    backStatus: 'WAIT_CONFIRM_TESTBATTERY'
                  }),
                onError: () => adjustment.unfreeze()
              }
            )
          },
          text: t(I18N_PREFIX + 'modal_cancel_action'),
          style: 'destructive'
        },
        {
          text: t(I18N_PREFIX + 'modal_cancel_cancel'),
          style: 'cancel'
        }
      ],
      <ExclamationTriangleIcon className='mb-2 h-12 w-12 text-red-500' />,
      null,
      true
    )
  }

  const failComplete = (err) => {
    if (err) notifyBugsnag(err)
    error(t(I18N_PREFIX + 'complete_error'))

    // Vor der Synthese darf weiter bearbeitet werden, es ist nichts wirksam
    // geworden. Danach nicht mehr: die Analyse ist gefaltet, eine weitere
    // Aenderung an override_defaults bliebe ohne einen zweiten
    // synthesizeAnalyze folgenlos.
    if (!synthesizedRef.current) adjustment.unfreeze()

    submittingRef.current = false
    setIsLoadingComplete(false)
  }

  const complete = async () => {
    if (submittingRef.current) return
    submittingRef.current = true
    setIsLoadingComplete(true)

    try {
      // Ab hier schreibt nichts mehr im Hintergrund: synthesizeAnalyze
      // ersetzt career_analyse vollstaendig, ein paralleler updateCareer
      // ginge verloren. Career kennt keine Optimistic Concurrency.
      await adjustment.freeze()

      // synthesizeAnalyze ist auf dem Preset-Pfad nicht idempotent, ein
      // zweiter Aufruf zerstoert career_preset_id und wirft danach. Scheitert
      // unten der Statuswechsel, wiederholt ein zweiter Klick deshalb
      // ausschliesslich diesen und nicht die Synthese.
      if (!synthesizedRef.current) {
        const draft = adjustment.draftRef.current

        // Ein autoritativer Write aus dem Draft. Er traegt auch eine
        // Aenderung, die noch im Debounce-Fenster hing, und heilt einen zuvor
        // fehlgeschlagenen Autosave.
        await mutation({
          mutation: 'updateCareer',
          input: {
            id: career.id,
            career_analyse: withOverrideDefaults(career.career_analyse, draft),
            app_settings: getAppSettings(
              career.app_settings,
              buildChallengesForAppSettings(draft, {
                configuration,
                availableExamIds
              })
            )
          }
        })

        await query({
          query: 'synthesizeAnalyze',
          variables: { career_id: career.id }
        })

        synthesizedRef.current = true
      }

      handleNextNavigation({
        nextStatus: 'WAIT_ACTIVE',
        // Der Statuswechsel laeuft ueber careerMutation, dessen Shim den
        // GraphQL-Fehler faengt und trotzdem resolved. handleNextNavigation
        // ist ausserdem synchron, dieser try-Block ist danach durch. Ohne
        // diesen Callback erfaehrt complete() vom Fehlschlag also nie und
        // laesst Spinner und eingefrorenen Draft stehen.
        onError: failComplete,
        onSuccess: () =>
          trackLeave(trackEvents.CREATE_CAREER_ADJUSTMENT_COMPLETED)
      })
    } catch (err) {
      failComplete(err)
    }
  }

  // Dauer und Anzahl der Challenges gehoeren zur ganzen Konfiguration und
  // stehen deshalb im Seitenkopf. Abgeleitet aus dem Draft, damit sie beim
  // Toggle sofort mitziehen.
  const stats = useMemo(() => {
    const playable = adjustment.selectedExamIds.filter(
      (exam_id) => !!system.challenges[exam_id]
    )

    const timeToComplete = playable.reduce(
      (total, exam_id) => total + system.challenges[exam_id].timeToComplete,
      0
    )

    const { minutes: duration } = convertNumberToTime(timeToComplete / 60000)

    return [
      {
        icon: ClockIcon,
        name: t('create_career.adjust_test_battery.duration_title'),
        stat: t('create_career.adjust_test_battery.duration_value', {
          duration
        })
      },
      {
        icon: PuzzlePieceIcon,
        name: t('create_career.adjust_test_battery.number_challenges_title'),
        stat: t(
          'create_career.adjust_test_battery.number_challenges_selected',
          {
            number: playable.length,
            total: availableExamIds.filter(
              (exam_id) => exam_id !== 'PATH_FINDER'
            ).length
          }
        )
      }
    ]
  }, [adjustment.selectedExamIds, availableExamIds, system.challenges, t])

  const announcement = useMemo(() => {
    if (adjustment.prunedCriterion) {
      return t(I18N_PREFIX + 'announcement_pruned', {
        dimension:
          system.dimensions_pdf_reports[adjustment.prunedCriterion]?.property[
            language
          ] || adjustment.prunedCriterion
      })
    }

    if (adjustment.saveState === 'saved') {
      return t(I18N_PREFIX + 'announcement_saved', {
        challenges: adjustment.selectedExamIds.length,
        criteria: adjustment.mandatoryCriteria.length
      })
    }

    if (adjustment.saveState === 'error') {
      return t(I18N_PREFIX + 'save_state_error')
    }

    return ''
  }, [
    adjustment.prunedCriterion,
    adjustment.saveState,
    adjustment.selectedExamIds,
    adjustment.mandatoryCriteria,
    system.dimensions_pdf_reports,
    language,
    t
  ])

  // Haengt an den Scores, nicht an override_defaults: readAdjustmentDraft
  // liefert auch ohne override_defaults einen vollstaendigen Draft, und ein
  // Zwischenzustand darf den ganzen Schritt nicht unmounten.
  if (!careerAnalyse.scores) return <Loader />

  return (
    <div className='mx-auto max-w-7xl'>
      <div className='mb-8 mt-10 flex flex-col gap-y-4 lg:flex-row lg:items-center lg:justify-between'>
        <h1 className='text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl'>
          {t(I18N_PREFIX + 'title')}
        </h1>
        {/* Die Kennzahlen beschreiben die ganze Konfiguration, nicht nur die
            Testbatterie. Oben bleiben sie im Blick, waehrend man unten im
            Grid Challenges an- und abwaehlt. */}
        <CompactStats items={stats} />
      </div>

      <div className='flex gap-x-16'>
        <SectionNav
          sections={sections}
          activeId={activeId}
          onSelect={handleSectionSelect}
        />

        <div className='mt-6 w-full min-w-0 space-y-16 xl:mt-0'>
          {/* scroll-mt haelt die Ueberschrift beim Sprung frei. */}
          <section id={SECTIONS[0].id} className='scroll-mt-8'>
            <CustomizationTestBattery
              configuration={configuration}
              availableExamIds={availableExamIds}
              selectedExamIds={adjustment.selectedExamIds}
              onToggleChallenge={toggleChallenge}
            />
          </section>

          <section id={SECTIONS[1].id} className='scroll-mt-8'>
            <HardnessOfSelection
              value={adjustment.selectionDifficulty}
              onChange={setSelectionDifficulty}
            />
          </section>

          <section id={SECTIONS[2].id} className='scroll-mt-8'>
            <MustCriteriaSelection
              dimensions={adjustment.dimensions}
              mandatoryCriteria={adjustment.mandatoryCriteria}
              onToggleCriterion={toggleCriterion}
            />
          </section>
        </div>
      </div>

      {/* Ausserhalb der Abschnitte, damit ein Sprung die Region nicht neu
          mountet und ihren alten Text erneut vorliest. */}
      <div aria-live='polite' className='sr-only'>
        {announcement}
      </div>

      <CreateCareerFooter>
        <div className='flex flex-col gap-y-3 sm:flex-row sm:items-center sm:justify-between sm:gap-y-0'>
          <SaveStateIndicator
            saveState={adjustment.saveState}
            onRetry={adjustment.retry}
            disabled={isLoadingComplete}
          />
          <div className='flex justify-end gap-x-2'>
            <Button.SecondaryXL
              text={t(I18N_PREFIX + 'abort')}
              onClick={abort}
              isLoading={isLoadingBack}
            />
            <Button.PrimaryXL
              text={t(I18N_PREFIX + 'complete')}
              onClick={complete}
              isLoading={isLoadingComplete}
            />
          </div>
        </div>
      </CreateCareerFooter>
    </div>
  )
}

Adjustment.propTypes = {
  career: PropTypes.object.isRequired,
  careerMutation: PropTypes.object.isRequired
}

export default Adjustment
